import { ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from 'react-aria-components';
import { Icon } from '@platform/components';

type PlanAccordionProps = {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  initiallyClosed?: boolean;
  isCompleted?: boolean;
  containerClassName?: string;
  customBorderClass?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const PlanAccordion = ({
  icon,
  title,
  subtitle,
  children,
  initiallyClosed = false,
  isCompleted = false,
  containerClassName,
  customBorderClass,
  isOpen: controlledIsOpen,
  onOpenChange,
}: PlanAccordionProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(!initiallyClosed);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalIsOpen(next);
    }
  };

  const prevIsCompleted = useRef(isCompleted);

  useEffect(() => {
    if (isControlled) return; // parent controls open state
    if (isCompleted) {
      setInternalIsOpen(false);
    } else if (prevIsCompleted.current && !isCompleted) {
      setInternalIsOpen(true);
    }
    prevIsCompleted.current = isCompleted;
  }, [isCompleted, isControlled]);

  const borderClass = customBorderClass ?? (isCompleted ? 'border-success-500' : isOpen ? 'border-brand-500' : 'border-gray-100');

  return (
    <div
      className={`flex flex-col items-start rounded-xl border-2 ${borderClass} ${containerClassName ?? 'bg-white'}`}
    >
      {/* Header */}
      <Button
        className='flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-4 outline-none data-[focus-visible]:ring-brand sm:px-6 sm:py-5'
        onPress={() => setIsOpen(!isOpen)}
      >
        {/* Left icon */}
        <div className='shrink-0'>{icon}</div>

        {/* Title + subtitle */}
        <div className='flex flex-1 flex-col items-start text-left'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className="text-lg font-medium leading-7 text-gray-700 sm:text-2xl sm:leading-8">
              {title}
            </span>
            {isCompleted && (
              <div className='flex items-center gap-1 rounded-full border border-success-100 bg-success-25 px-2 py-0.5'>
                <Icon type='check' size='14' stroke='success-500' />
                <span className="text-xs font-medium leading-[18px] text-success-500">
                  Completed
                </span>
              </div>
            )}
          </div>
          {subtitle && (
            <span className="text-base font-normal leading-6 text-gray-500">
              {subtitle}
            </span>
          )}
        </div>

        {/* Chevron */}
        <div className='ml-auto shrink-0'>
          <Icon
            type={isOpen ? 'chevron-up' : 'chevron-down'}
            size='24'
            additionalClassNames='stroke-gray-400'
          />
        </div>
      </Button>

      {/* Content */}
      {isOpen && children != null && (
        <div className='flex w-full flex-col gap-6 px-4 pb-4 sm:px-6 sm:pb-6'>
          {children}
        </div>
      )}
    </div>
  );
};
